/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import { useFormik } from "formik";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import * as yup from "yup";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { RootState, useAppDispatch } from "@store/store";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { transformTitleCase } from "@helpers/funciones";
import { PayloadPersonPatch, StateStructure } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import {
  useLazyGetPersonByIdGuaranteeQuery,
  usePatchPersonSecondGuaranteeMutation,
  usePatchUsuarioStatusSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { useLazyGetStatesGuaranteeQuery } from "@api/second_guarantee_api/catalogsSecondGuarantee";
import { setCurretStepSecondGuarantee, setUserSecondGuaranteePersonalInfo } from "@store/slices/segundoAvalSlice";
import { useNextStepAndPatch } from "@hooks/useStatusConfigSecondGuarantee";

const maxDate = dayjs().subtract(18, "years");

const RegistroSegundoAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();
  const shareholderData = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const { nextStatus } = useNextStepAndPatch(shareholderData.status_config);

  const dispatch = useAppDispatch();
  const [getStatesTrigger] = useLazyGetStatesGuaranteeQuery();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();
  const [patchNextStatus] = usePatchUsuarioStatusSecondGuaranteeMutation();
  const [patchPerson] = usePatchPersonSecondGuaranteeMutation();

  const [selectedDate, setSelectedDate] = useState<any>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [placesCatalog, setPlacesCatalog] = useState<StateStructure[]>([]);
  const [date, setDate] = useState("");

  const [rfc, setRfc] = useState("");
  const [curp, setCurp] = useState("");

  const formik = useFormik({
    initialValues: {
      name: "",
      first_lastname: "",
      second_lastname: "",
      date_birth: "",
      birth_state: "",
      email: "",
      sex: "",
      code_phone_number: "+52",
      phone_number: "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido"),
      first_lastname: yup.string().required("Campo requerido"),
      second_lastname: yup.string(),
      date_birth: yup
        .string()
        .required("Campo requerido")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("is-adult", "Debes ser mayor de 18 años", (value) => {
          if (!value) return false;

          const birthDate = dayjs(value);
          const age = dayjs().diff(birthDate, "years");

          return age >= 18;
        }),
      birth_state: yup.string().required("Campo requerido"),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Debe seleccionar un género").required("Campo requerido"),
      code_phone_number: yup.string().required("Campo requerido"),
      phone_number: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        const payloadShareholderData: PayloadPersonPatch = {
          name: values.name,
          last_name: values.first_lastname,
          last_name_2: values.second_lastname,
          birth_date: dayjs(values.date_birth).format("YYYY-MM-DD"),
          birth_state: Number(values.birth_state),
          email: values.email,
          sex: values.sex,
          rfc: rfc,
          curp: curp,
          code_phone_number: values.code_phone_number,
          phone_number: values.phone_number,
        };

        await patchPerson({
          personId: String(shareholderData.id),
          body: payloadShareholderData,
        }).unwrap();

        dispatch(
          setUserSecondGuaranteePersonalInfo({
            name: values.name,
            first_lastname: values.first_lastname,
            second_lastname: values.second_lastname,
            date_birth: dayjs(values.date_birth).format("YYYY-MM-DD"),
            birth_state: values.birth_state,
            code_phone_number: values.code_phone_number,
            phone_number: values.phone_number,
            curp: curp,
            rfc: rfc,
            rfc_homoclave: rfc.slice(10, 13),
            sex: values.sex,
            email: values.email,
          }),
        );

   
          await patchNextStatus({
            usuario: String(shareholderData.user_id),
            body: {
              status_id: String(nextStatus?.status_id),
            },
          }).unwrap();
        

        dispatch(
          setCurretStepSecondGuarantee({
            code: String(nextStatus?.code),
            url: String(nextStatus?.url),
            sequence: Number(nextStatus?.sequence),
            status_id: String(nextStatus?.status_id),
          }),
        );

        navigate(String(nextStatus?.url));
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "RegistroSegundoAval",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [responseStates, person] = await Promise.all([
          getStatesTrigger().unwrap(),
          getPerson(String(shareholderData.id)).unwrap(),
        ]);

        setPlacesCatalog(responseStates);

        formik.setFieldValue("name", person.name || "");
        formik.setFieldValue("first_lastname", person.last_name || "");
        formik.setFieldValue("second_lastname", person.last_name_2 || "");

        formik.setFieldValue("birth_state", person.birth_state || "");

        formik.setFieldValue("email", person.email || "");
        formik.setFieldValue("sex", person.sex || "");
        formik.setFieldValue("code_phone_number", person.code_phone_number || "");
        formik.setFieldValue("phone_number", person.phone_number || "");
        setDate(dayjs(person.birth_date).format("YYYY-MM-DD") || "");
        setRfc(person.rfc || "");
        setCurp(person.curp || "");
        setTimeout(() => {
          formik.validateForm();
        }, 500);
      } catch (error) {
        console.error("Error al obtener los datos iniciales", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (date) {
      setSelectedDate(dayjs(date));
      formik.setFieldValue("date_birth", date);
    }
  }, [date]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box
          sx={{
            mt: isMobileOrTablet ? 2 : 4,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            Registro Aval
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <FormControl fullWidth>
                <TextField
                  id="name"
                  name="name"
                  label="Nombre(s)"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("name", transformTitleCase(e.target.value))}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name ? formik.errors.name : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="first_lastname"
                  name="first_lastname"
                  label="Apellido paterno"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("first_lastname", transformTitleCase(e.target.value))}
                  value={formik.values.first_lastname}
                  error={formik.touched.first_lastname && Boolean(formik.errors.first_lastname)}
                  helperText={formik.touched.first_lastname ? formik.errors.first_lastname : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="second_lastname"
                  name="second_lastname"
                  label="Apellido materno"
                  margin="normal"
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("second_lastname", transformTitleCase(e.target.value))}
                  value={formik.values.second_lastname}
                  error={formik.touched.second_lastname && Boolean(formik.errors.second_lastname)}
                  helperText={formik.touched.second_lastname ? formik.errors.second_lastname : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                  <DatePicker
                    label="Fecha de nacimiento"
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      const formattedDate = dayjs(date).format("YYYY-MM-DD");

                      formik.setFieldValue("date_birth", formattedDate);

                      formik.validateField("date_birth");
                    }}
                    maxDate={maxDate}
                    slotProps={{
                      textField: {
                        name: "date_birth",
                        onBlur: formik.handleBlur,
                        error: formik.touched.date_birth && Boolean(formik.errors.date_birth),
                        helperText: formik.touched.date_birth ? formik.errors.date_birth : "",
                        required: true,
                      },
                    }}
                    disabled={formik.isSubmitting}
                  />
                </LocalizationProvider>
              </FormControl>
              <FormControl
                fullWidth
                required
                sx={{ mt: 2 }}
                error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
              >
                <InputLabel id="birth_state-label">Lugar de nacimento</InputLabel>
                <Select
                  labelId="birth_state-label"
                  id="birth_state"
                  name="birth_state"
                  fullWidth
                  required
                  label="Lugar de nacimento"
                  value={formik.values.birth_state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.birth_state && Boolean(formik.errors.birth_state)}
                  disabled={formik.isSubmitting}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  {placesCatalog.map((item: StateStructure) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(formik.touched.birth_state) && Boolean(formik.errors.birth_state) && (
                  <FormHelperText>{formik.errors.birth_state}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <TextField
                  id="email"
                  name="email"
                  label="Correo electrónico"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email ? formik.errors.email : ""}
                  inputProps={{ maxLength: 100 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={5}>
                  <CustomContryCode
                    label="Código"
                    name="code_phone_number"
                    value={formik.values.code_phone_number}
                    onChange={(ev) => {
                      formik.handleChange(ev);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code_phone_number && Boolean(formik.errors.code_phone_number)}
                    disabled={formik.isSubmitting}
                    required={true}
                  />
                </Grid>
                <Grid item xs={7}>
                  <FormControl fullWidth>
                    <TextField
                      id="phone_number"
                      name="phone_number"
                      label="Teléfono"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={(ev) => {
                        formik.handleChange(ev);
                      }}
                      value={formik.values.phone_number}
                      error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                      helperText={formik.touched.phone_number ? formik.errors.phone_number : ""}
                      inputProps={{ maxLength: 10 }}
                      disabled={formik.isSubmitting}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl
                component="fieldset"
                sx={{ mt: 1 }}
                error={formik.touched.sex && formik.errors.sex ? true : false}
              >
                <FormLabel component="legend">Sexo como aparece en tu INE</FormLabel>
                <RadioGroup aria-label="sex" name="sex" value={formik.values.sex} onChange={formik.handleChange} row>
                  <FormControlLabel
                    sx={{ color: theme?.palette?.primary?.dark }}
                    value="MASCULINO"
                    control={<Radio />}
                    label="Masculino"
                    disabled={formik.isSubmitting}
                  />
                  <FormControlLabel
                    sx={{ color: theme?.palette?.primary?.dark }}
                    value="FEMENINO"
                    control={<Radio />}
                    label="Femenino"
                    disabled={formik.isSubmitting}
                  />
                </RadioGroup>
                {formik.touched.sex && formik.errors.sex ? <FormHelperText>Selecciona tu sexo</FormHelperText> : null}
              </FormControl>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={!formik.isValid || isLoading || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default RegistroSegundoAval;
